import { useEffect, useRef, useState } from "react";

interface UseFadeInOptions {
  delay?: number;
  threshold?: number;
  rootMargin?: string;
}

export const useFadeIn = (options: UseFadeInOptions = {}) => {
  const { delay = 0, threshold = 0.1, rootMargin = "0px 0px -50px 0px" } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    /**
     * Sem IntersectionObserver o conteudo APARECE, nao desaparece.
     *
     * .fade-up e opacity: 0 ate ganhar .visible. Antes de 20/10/2026, num
     * ambiente sem a API, este efeito lancava ReferenceError de dentro de um
     * passive effect — o React derruba a arvore e a pagina fica branca — e,
     * se nao lancasse, o bloco ficaria invisivel para sempre. Nenhum
     * navegador de gente cai aqui desde 2019, mas renderizador, prerender e
     * jsdom caem, e o modo de falhar nao pode ser esconder o texto.
     */
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Apply delay before showing
          if (delay > 0) {
            setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          // Disconnect after triggering once
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [delay, threshold, rootMargin]);

  return { ref, isVisible };
};

// Hook for staggered children animations
export const useStaggeredFadeIn = (itemCount: number, baseDelay = 0, stagger = 100) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Mesmo motivo do useFadeIn acima: falhar mostrando, nao escondendo.
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Generate delay for each item
  const getItemDelay = (index: number) => baseDelay + index * stagger;

  return { ref, isVisible, getItemDelay };
};
